import { useState, useMemo, useEffect } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { SearchOutlined as SearchOutlinedIcon } from "@mui/icons-material";
import { matchSorter } from "match-sorter";
import _ from "lodash";

const SelectWithFilter = ({
  placeholder,
  label,
  name,
  onChange,
  error,
  helperText,
  required,
  list,
  onPicked = _.identity,
  minInputLength,
  matcherOptions,
  renderInputProps,
  addMedicine,
  disabledAddMedicine,
  onAsyncSearch,
  ...props
}) => {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState(list || []);
  const [inputValue, setInputValue] = useState("");
  
  const labelId = `${name}-label`;
  const labelWithRequired = `${label}${required ? "*" : ""}`;

  const handleAsyncFilterOptions = useMemo(
    () =>
      _.debounce(async (searchValue) => {
        try {
          const results = await onAsyncSearch(searchValue);
          setOptions(results);
        } catch (error) {
          console.error('Async search error:', error);
        } finally {
          setLoading(false);
        }
      }, 400),
    [onAsyncSearch, minInputLength]
  );

  useEffect(() => {
    if (!onAsyncSearch) return;
    if (!inputValue || inputValue.length < minInputLength) return;
        
    setLoading(true);
    handleAsyncFilterOptions(inputValue);
  }, [inputValue, handleAsyncFilterOptions, onAsyncSearch]);

  const handleSyncFilterOptions = (options, { inputValue }) => {
    if (inputValue.length < minInputLength) return [];

    return matchSorter(options, inputValue, matcherOptions);
  };

  const filterOptions = !onAsyncSearch && minInputLength && {
    filterOptions: handleSyncFilterOptions,
  };

  return (
    <Autocomplete
      disablePortal
      id={labelId}
      disabled={addMedicine && disabledAddMedicine}
      onChange={(event, newValue) => {
        onChange(newValue);
        if (addMedicine !== undefined) {
          onChange(newValue);
          addMedicine();
        }
        if (
          event.type !== "click" &&
          event.key !== "Enter" &&
          addMedicine === undefined
        )
          return;
        onPicked(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
        if (!props.freeSolo) return;
        onChange(newInputValue);
      }}
      options={onAsyncSearch ? (loading ? [] : options) : list}
      renderInput={(params) => (
        <TextField
          {...params}
          {...renderInputProps}
          label={labelWithRequired}
          error={!_.isEmpty(error)}
          helperText={helperText}
          placeholder={placeholder ? placeholder : undefined}
          InputProps={{
            ...params.InputProps,
            ...renderInputProps?.InputProps,
            ...(placeholder && {
              startAdornment: <SearchOutlinedIcon color="primary" />,
            }),
          }}
        />
      )}
      noOptionsText="No hay opciones que coincidan con la búsqueda"
      renderOption={(props, value) => {
        return (
          <li {...props} key={_.get(value, "id", value)}>
            {props.key}
          </li>
        );
      }}
      loadingText="Cargando..."
      loading={loading}
      {...filterOptions}
      {...props}
    />
  );
};

export default SelectWithFilter;